import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  List,
  ListItem,
  Avatar,
  IconButton,
  CircularProgress,
  Alert,
  Container,
  Divider,
  Chip,
  Card,
  CardContent,
  Snackbar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import MessageApi from '../../api/MessageApi';
import UserApi from '../../api/UserApi';
import { FullMessageDto, CreateMessageDto } from '../../interfaces/MessageInterfaces';
import { DateTime } from 'luxon';
import WebSocketService from '../../utils/WebSocketService';
import './ManagerMessageView.scss';

interface ManagerMessageViewProps {
  /** The client whose thread to show. Defaults to `?User_guid=` in the page address. */
  userGuid?: string;
  /**
   * Shown inside a case tab rather than as its own page (Lloyd, 6 Oct 2026: "can we get a similar
   * one for messages?"). Drops the back button and the full-height page sizing.
   */
  embedded?: boolean;
}

const ManagerMessageView: React.FC<ManagerMessageViewProps> = ({ userGuid: userGuidProp, embedded = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const userGuid = userGuidProp ?? searchParams.get('User_guid');
  const [user, setUser] = useState<any>(null);
  // Tracks the client lookup specifically. `loading` belongs to the message fetch, and gating the
  // header on that showed "Client" before the lookup had even been tried.
  const [nameResolved, setNameResolved] = useState(false);
  const [messages, setMessages] = useState<FullMessageDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<number>(0);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Load messages for the specific user
  const loadMessages = useCallback(async (from: number = 0, to: number = 15, prepend: boolean = false) => {
    if (!userGuid) return;

    try {
      if (!prepend) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const fetchedMessages = await MessageApi.getMessages(from, to, userGuid);
      
      if (fetchedMessages.length === 0) {
        setHasMoreMessages(false);
        return;
      }

      // Sort messages by creation date (oldest first)
      fetchedMessages.sort((a, b) => 
        new Date(a.Message.Message_CreatedAt).getTime() - new Date(b.Message.Message_CreatedAt).getTime()
      );

      if (prepend) {
        // When loading older messages, prepend them and maintain scroll position
        const container = messagesContainerRef.current;
        const oldScrollHeight = container?.scrollHeight || 0;
        
        setMessages(prev => [...fetchedMessages, ...prev]);
        
        // Restore scroll position after new messages are loaded
        setTimeout(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            const scrollDiff = newScrollHeight - oldScrollHeight;
            container.scrollTop = scrollPositionRef.current + scrollDiff;
          }
        }, 100);
      } else {
        setMessages(fetchedMessages);
        // Mark all messages as seen by manager
        fetchedMessages.forEach(message => {
          if (!message.Message.Message_ManagerSeen) {
            MessageApi.markMessageAsSeen(message.Message.Message_guid).catch(console.error);
          }
        });
        // Scroll to bottom for initial load
        setTimeout(scrollToBottom, 100);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userGuid]);

  // Load more messages when scrolled to top
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current || loadingMore || !hasMoreMessages) return;

    const container = messagesContainerRef.current;
    if (container.scrollTop === 0 && messages.length > 0) {
      // Store current scroll position
      scrollPositionRef.current = container.scrollTop;
      // Load older messages
      loadMessages(messages.length, messages.length + 15, true);
    }
  }, [loadingMore, hasMoreMessages, messages.length, loadMessages]);

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || sending || !userGuid) return;

    try {
      setSending(true);
      
      const createMessageDto: CreateMessageDto = {
        Message_Body: newMessage.trim(),
        User_guid: userGuid
      };

      const sentMessage = await MessageApi.sendMessage(createMessageDto);
      
      // Add message to local state (it will also come via WebSocket)
      setMessages(prev => [...prev, sentMessage]);
      setNewMessage('');
      setTimeout(scrollToBottom, 100);
      
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Format message timestamp
  const formatMessageTime = (timestamp: Date | string) => {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    const dt = DateTime.fromJSDate(date);
    const now = DateTime.now();
    
    if (dt.hasSame(now, 'day')) {
      return dt.toFormat('h:mm a');
    } else if (dt > now.minus({ days: 7 })) {
      return dt.toFormat('ccc h:mm a');
    } else {
      return dt.toFormat('MMM d, h:mm a');
    }
  };

  // Get sender name
  const getSenderName = (message: FullMessageDto) => {
    if (message.Manager) {
      return `${message.Manager.Manager_FirstName} ${message.Manager.Manager_LastName}`;
    } else if (message.Message.Message_Source === 'system') {
      return 'TX Ticket Doctor';
    } else {
      return `${message.User.User_FirstName} ${message.User.User_LastName}`;
    }
  };

  // Check if message is from a manager or an automated send — both sit on the staff side of the
  // thread. A system message shares the client's `Manager_id: null` shape, so it has to be told
  // apart by Message_Source rather than by who it's attached to.
  const isManagerMessage = (message: FullMessageDto) => {
    return !!message.Manager || message.Message.Message_Source === 'system';
  };

  // WebSocket effects
  useEffect(() => {
    if (!userGuid) return;

    const handleNewMessage = (message: FullMessageDto) => {
      // Only add messages for this specific user conversation
      if (message.Message.User_guid === userGuid) {
        setMessages(prev => {
          // Check if message already exists to avoid duplicates
          const exists = prev.some(m => m.Message.Message_guid === message.Message.Message_guid);
          if (exists) return prev;
          
          return [...prev, message];
        });
        setTimeout(scrollToBottom, 100);
        
        // Mark as seen by manager if it's from user
        if (!message.Manager) {
          MessageApi.markMessageAsSeen(message.Message.Message_guid).catch(console.error);
        }
      }
    };

    // Connect to WebSocket and set up listeners
    WebSocketService.connect();
    WebSocketService.joinManagerRoom();
    // Only listen to onNewUserMessage for managers - this handles messages from users
    WebSocketService.onNewUserMessage(handleNewMessage);

    return () => {
      WebSocketService.offNewUserMessage(handleNewMessage);
      WebSocketService.leaveManagerRoom();
    };
  }, [userGuid]); // Only depend on userGuid to prevent re-runs

  // Initial load
  useEffect(() => {
    if (!userGuid) {
      setError('User GUID not found in URL parameters');
      return;
    }
    loadMessages();
  }, [userGuid, loadMessages]);

  // The client's name used to be read off the first message, so a conversation with no messages
  // never named the person being written to, and sending the first one did not fix it either. Read
  // it from the client record instead, which does not depend on there being any history.
  useEffect(() => {
    if (!userGuid) return;
    let abandoned = false;
    setNameResolved(false);

    UserApi.getUsersForManager({ User_guid: userGuid })
      .then((users) => {
        if (abandoned) return;
        if (users.length > 0) setUser(users[0]);
      })
      // Logged rather than shown: the thread is readable and repliable without the name, so an
      // error banner over a working conversation would be the worse outcome.
      .catch(console.error)
      .finally(() => {
        if (!abandoned) setNameResolved(true);
      });

    return () => {
      abandoned = true;
    };
  }, [userGuid]);

  // Global cleanup on component unmount
  useEffect(() => {
    return () => {
      // Clean up any remaining WebSocket listeners on unmount
      WebSocketService.leaveManagerRoom();
    };
  }, []);

  // Add scroll listener
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  if (!userGuid) {
    return (
      <>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error">User GUID not found in URL parameters.</Alert>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Container>
      </>
    );
  }

  return (
    <>
      <Container
        maxWidth={embedded ? false : 'md'}
        disableGutters={embedded}
        className="manager-messages-container"
        sx={embedded ? { height: 640, minHeight: 0, py: 0 } : undefined}
      >
        <Paper elevation={2} className="messages-paper">
          {/* Header */}
          <Box className="messages-header">
            <Box className="header-content">
              {!embedded && (
                <IconButton
                  color="inherit"
                  onClick={() => navigate(-1)}
                  className="back-button"
                >
                  <ArrowBackIcon />
                </IconButton>
              )}
              <Box className="user-info">
                <Typography variant="h6" component="h1">
                  {/* Not 'Loading...' once the lookup has finished: a name that never arrives left
                      the header loading forever, which is the state this replaced. */}
                  {user
                    ? `${user.User_FirstName} ${user.User_LastName}`
                    : nameResolved
                      ? 'Client'
                      : 'Loading...'}
                </Typography>
                <Typography variant="body2" className="user-guid">
                  {userGuid}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Messages Container */}
          <Box 
            ref={messagesContainerRef}
            className="messages-scroll-container"
          >
            {/* Loading indicator for older messages */}
            {loadingMore && (
              <Box className="loading-more">
                <CircularProgress size={20} />
                <Typography variant="caption" sx={{ ml: 1 }}>
                  Loading older messages...
                </Typography>
              </Box>
            )}

            {/* Messages List */}
            {loading && messages.length === 0 ? (
              <Box className="messages-loading">
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading messages...
                </Typography>
              </Box>
            ) : messages.length === 0 ? (
              <Box className="no-messages">
                <Typography variant="body2" color="text.secondary">
                  No messages yet. Start the conversation with {user?.User_FirstName || 'this user'}!
                </Typography>
              </Box>
            ) : (
              <List className="messages-list">
                {messages.map((message, index) => {
                  const isManager = isManagerMessage(message);
                  const senderName = getSenderName(message);
                  
                  return (
                    <ListItem 
                      key={message.Message.Message_guid}
                      className={`message-item ${isManager ? 'manager-message' : 'user-message'}`}
                    >
                      <Box className={`message-wrapper ${isManager ? 'manager-message' : 'user-message'}`}>
                        {isManager && (
                          <Avatar className="message-avatar manager-avatar">
                            <SupportAgentIcon />
                          </Avatar>
                        )}
                        
                        {!isManager && (
                          <Avatar className="message-avatar user-avatar">
                            <PersonIcon />
                          </Avatar>
                        )}
                        
                        <Box className="message-content">
                          <Typography variant="caption" className="message-sender">
                            {senderName}
                          </Typography>
                          
                          <Card className="message-bubble">
                            <CardContent>
                              <Typography variant="body2">
                                {message.Message.Message_Body}
                              </Typography>
                            </CardContent>
                          </Card>
                          
                          <Typography variant="caption" className="message-time">
                            {formatMessageTime(message.Message.Message_CreatedAt)}
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                  );
                })}
              </List>
            )}
            
            <div ref={messagesEndRef} />
          </Box>

          {/* Message Input */}
          <Box className="message-input-container">
            <TextField
              fullWidth
              multiline
              maxRows={4}
              variant="outlined"
              placeholder={`Message ${user?.User_FirstName || 'user'}...`}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={sending}
              className="message-input"
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={!newMessage.trim() || sending}
              className="send-button"
            >
              {sending ? <CircularProgress size={20} /> : <SendIcon />}
            </IconButton>
          </Box>
        </Paper>

        {/* Snackbar for errors */}
        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={() => setError(null)}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>

        {/* Snackbar for success */}
        <Snackbar 
          open={!!success} 
          autoHideDuration={3000} 
          onClose={() => setSuccess(null)}
        >
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default ManagerMessageView;